<template>
  <main class="flex flex-col gap-5 text-black">
    <div
      v-if="role"
      class="flex justify-between flex-wrap items-center col-span-12"
    >
      <div class="flex items-center gap-5">
        <h4>
          {{ $t("objectives.titles.materials") }}
        </h4>
        <Button
          :label="$t('objects.add')"
          class="btn-outline p-button-sm"
          icon="pi pi-plus"
          @click="goToMaterial(objectiveId, { type: 'add' })"
          v-if="role === 'TEACHER'"
        />
      </div>
      <div class="flex items-center gap-2" v-if="role === 'TEACHER'">
        <Button
          :label="$t('labels.save')"
          :disabled="data?.length == 0"
          :loading="loading"
          class="btn-primary p-button-sm"
          @click="nextStep"
        />
      </div>
    </div>

    <InfiniteScroll @onScroll="onScroll">
      <template #header>
        <ListHeader @onSelect="onFilter" @onSearch="onSearch" />
      </template>
      <template #content>
        <Loader v-if="loader" :loader="loader" loaderSize="h-full" />
        <EmptyData
          v-else-if="!data || data.length === 0"
          :data="data?.length === 0"
          :title="emptyState.title"
          :label="emptyState.description"
          :image="emptyState.image"
          imgSize="h-40"
        />
        <section v-else>
          <div
            id="content"
            class="bg-white"
            v-for="(item, index) in data"
            :key="index"
          >
            <ListContent
              :data="item"
              :role="role"
              :index="index"
              :allData="data"
              v-model:loader="loadingDelete"
              @onEdit="
                goToMaterial(objectiveId, {
                  id: item.id,
                  type: 'edit',
                })
              "
              @onDelete="deleteMaterial(item.id)"
            />
          </div>
        </section>
      </template>
    </InfiniteScroll>
  </main>
</template>

<script lang="ts">
import { Material } from "@/models/material.model";
import { MaterialsRest } from "@/services/rest/materials.rest";
import { GenericsUtils } from "@/utils/generics.util";
import { GoToRoute } from "@/utils/go-to.util";
import { ChaptersService } from "@/views/Chapters/chapters.service";
import { take } from "rxjs";
import { computed, defineAsyncComponent, defineComponent } from "vue";
import { MaterialsService } from "./materials.service";

const ListContent = defineAsyncComponent(
  () => import("./List/list-content.vue")
);
const ListHeader = defineAsyncComponent(() => import("./List/list-header.vue"));

export default defineComponent({
  name: "materials",
  components: { ListHeader, ListContent },
  emits: [
    "onNext",
    "onDeleted",
    "onFilter",
    "onSearch",
    "onScroll",
    "onAllow",
    "update:loading",
  ],
  computed: {
    rest(): MaterialsRest {
      return new MaterialsRest();
    },
    serviceChapter(): ChaptersService {
      return new ChaptersService();
    },
    go(): GoToRoute {
      return new GoToRoute();
    },
  },
  props: {
    role: String,
    loader: Boolean,
  },
  setup() {
    const generics = computed(() => new GenericsUtils());
    return { generics };
  },
  data() {
    return {
      data: [] as Material[],
      objectiveId: this.$route.params.id as any,
      loading: false as boolean,
      loadingDelete: false as boolean,
      params: {
        courseId: this.$route.params.courseId,
        chapterId: this.$route.params.chapterId,
        id: this.$route.params.id,
        tab: 2,
      },
      emptyState: {
        image: "empty_objects.svg",
        title: this.$t("objects.empty.title"),
        description: this.$t("objects.empty.description"),
      } as any,
    };
  },
  mounted() {
    this.getAllMaterials();
  },
  unmounted() {
    MaterialsService.materials$.next([]);
    this.onFilter("");
    this.onSearch("");
  },
  watch: {
    $route() {
      MaterialsService.materials$.next([]);
      this.onFilter("");
      this.onSearch("");
    },
  },
  methods: {
    getAllMaterials(): void {
      MaterialsService.materials$.pipe(take(2)).subscribe({
        next: (response) => {
          this.data.push(...response);
        },
        error: () => {
          this.data.push(...[]);
        },
      });
    },
    goToMaterial(
      objectiveId: string,
      optional: { id?: string; type?: string }
    ): void {
      const params = {
        objectiveId: objectiveId,
        id: optional.id ?? undefined,
        type: optional.type,
      };
      this.go.redirectToPage("formObject", params);
    },
    deleteMaterial(materialId: string): void {
      this.loadingDelete = true;
      this.rest.deleteMaterial(this.objectiveId, materialId).subscribe({
        next: () => {
          const materials = this.data;
          const filtered = materials.filter((e: any) => e.id !== materialId);
          this.data = filtered;
          setTimeout(() => {
            this.$emit("onDeleted", this.data.length);
          }, 300);
        },
        error: () => {
          this.loadingDelete = false;
        },
        complete: () => {
          this.loadingDelete = false;
        },
      });
    },
    onFilter(event: any): void {
      this.data.length = 0;
      (this.$refs.infiniteScroll as HTMLDivElement)?.scrollTo(0, 0);
      this.$emit("onFilter", event);
    },
    onSearch(event: any): void {
      this.data.length = 0;
      (this.$refs.infiniteScroll as HTMLDivElement)?.scrollTo(0, 0);
      this.$emit("onSearch", event);
    },
    nextStep(): void {
      if (this.data.length !== 0) {
        this.loading = true;
        setTimeout(() => {
          this.$emit("onNext", 2);
          this.$emit("onAllow", 1);
          this.go.redirectToPage("formObjective", this.params);
          this.loading = false;
        }, 500);
      }
    },
    onScroll(event: any): void {
      this.$emit("onScroll", event);
    },
  },
});
</script>
